<template>
    <div class="card p-shadow-app">
        <Toast />

        <div class="p-mb-3 p-d-flex p-jc-between p-ai-center">
            <Button
                v-if="can('addEditPatInternalDossiers')"
                label="Novo Dossiê"
                icon="pi pi-plus"
                @click="openForm"
            />
        </div>

        <div class="p-fluid p-formgrid p-grid p-mb-3">
            <div class="p-field p-col-12 p-md-3">
                <label for="tipodoc">Tipo de Dossiê</label>
                <Dropdown
                    id="tipodoc"
                    v-model="tipodoc"
                    :options="dossierTypes"
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Selecione o tipo"
                    :loading="loadingTypes"
                    showClear
                />
            </div>

            <div class="p-field p-col-12 p-md-3">
                <label for="obrano">Número de Dossiê</label>
                <InputText
                    id="obrano"
                    v-model="obrano"
                    placeholder="Filtrar por número de dossiê" />
            </div>

            <div class="p-field p-col-12 p-md-2">
                <label for="dateFrom">Data De</label>
                <Calendar
                    id="dateFrom"
                    v-model="filters.dateFrom"
                    dateFormat="dd/mm/yy"
                    placeholder="Data inicial"
                />
            </div>

            <div class="p-field p-col-12 p-md-2">
                <label for="dateTo">Data Até</label>
                <Calendar
                    id="dateTo"
                    v-model="filters.dateTo"
                    dateFormat="dd/mm/yy"
                    placeholder="Data final"
                />
            </div>

            <div class="p-field p-col-12 p-md-2 p-d-flex p-ai-end">
                <Button
                    label="Aplicar Filtros"
                    icon="pi pi-filter"
                    @click="fetchWithFilters"
                />
            </div>
        </div>

        <DataTable
            :value="dossierList"
            :loading="loading"
            :paginator="true"
            :rows="perPage"
            :rowsPerPageOptions="[10, 20, 50, 100]"
            responsiveLayout="scroll"
            class="p-datatable-sm"
            stripedRows
            :filters.sync="tableFilters"
            filterDisplay="menu"
            :lazy="true"
            :totalRecords="totalRecords"
            @filter="fetchDossiers"
            @sort="fetchDossiers"
            @page="fetchDossiers"
            :expandedRows.sync="expandedRows"
            @row-expand="onRowExpand"
            @row-collapse="onRowCollapse"
        >
            <template #empty>
                <div class="p-text-center p-p-3">
                    <i class="pi pi-inbox" style="font-size: 3rem; color: #999;"></i>
                    <p class="p-mt-2">Nenhum dossiê encontrado</p>
                </div>
            </template>

            <Column :expander="true" />

            <Column field="id" header="Referência" :sortable="true" style="min-width: 150px">
                <template #body="slotProps">
                    <strong>{{ slotProps.data.id }}</strong>
                </template>
            </Column>

            <Column field="type_name" header="Tipo" :sortable="true" style="min-width: 180px">
                <template #body="slotProps">
                    <Tag :value="slotProps.data.type_name" severity="info" />
                </template>
            </Column>

            <Column field="name" header="Nome/Cliente/Fornecedor" :sortable="true" style="min-width: 200px">
                <template #body="slotProps">
                    {{ slotProps.data.name || '-' }}
                </template>
            </Column>

            <Column field="date" header="Data" :sortable="true" style="min-width: 120px">
                <template #body="slotProps">
                    {{ formatDate(slotProps.data.date) }}
                </template>
            </Column>

            <Column field="observation" header="Observações" style="min-width: 200px">
                <template #body="slotProps">
                    <span class="p-text-truncate" style="max-width: 200px; display: block;">
                        {{ slotProps.data.observation || '-' }}
                    </span>
                </template>
            </Column>

            <Column field="closed" header="Estado" :sortable="true" style="min-width: 100px">
                <template #body="slotProps">
                    <Tag
                        :value="slotProps.data.closed ? 'Fechado' : 'Aberto'"
                        :severity="slotProps.data.closed ? 'success' : 'warning'"
                    />
                </template>
            </Column>

            <Column field="subtotal" header="Total" :sortable="true" style="min-width: 120px">
                <template #body="slotProps">
                    {{ formatCurrency(slotProps.data.subtotal) }}
                </template>
            </Column>

            <Column field="total" header="Total (c/iva)" :sortable="true" style="min-width: 120px">
                <template #body="slotProps">
                    {{ formatCurrency(slotProps.data.total) }}
                </template>
            </Column>

            <Column header="Ações" style="min-width: 150px">
                <template #body="slotProps">
                    <div class="p-d-flex p-gap-2">
                        <Button
                            icon="pi pi-pencil"
                            class="p-button-rounded p-button-success p-button-text"
                            v-tooltip.top="'Editar'"
                            @click="editDossier(slotProps.data)"
                        />
                        <Button
                            icon="pi pi-copy"
                            class="p-button-rounded p-button-primary p-button-text"
                            v-tooltip.top="'Converter para outro tipo'"
                            @click="openConvertDialog(slotProps.data)"
                        />
                        <PreviewInternalDossierReport
                            v-if="slotProps.data.type_id === 3"
                            :report="'commercial_offer'"
                            :stamp="slotProps.data.reference"
                        />

                        <PreviewInternalDossierReport
                            v-if="slotProps.data.type_id === 19"
                            :report="'spares_orders'"
                            :stamp="slotProps.data.reference"
                        />

                        <!-- <Button
                            icon="pi pi-trash"
                            class="p-button-rounded p-button-danger p-button-text"
                            v-tooltip.top="'Eliminar'"
                            @click="confirmDelete(slotProps.data)"
                            :disabled="slotProps.data.closed"
                        /> -->
                    </div>
                </template>
            </Column>

            <template #expansion="slotProps">
                <div class="p-4" v-if="slotProps.data.lines && slotProps.data.lines.length">
                    <DataTable :value="slotProps.data.lines" responsiveLayout="scroll" class="p-datatable-sm">
                        <Column v-for="(code, index) in Object.keys(expandedViewsFields)"
                            :key="index + '-' + code"
                            :field="code"
                            :header="expandedViewsFields[code].label">
                            <template #body="lineSlotProps">
                                <span v-if="expandedViewsFields[code].type === 'currency'">
                                    {{ formatCurrency(lineSlotProps.data[code]) }}
                                </span>
                                <span v-else-if="expandedViewsFields[code].type === 'percentage'">
                                    {{ formatDiscount(lineSlotProps.data[code]) }}
                                </span>
                                <span v-else>
                                    {{ lineSlotProps.data[code] ?? '-' }}
                                </span>
                            </template>
                        </Column>
                    </DataTable>
                </div>
                <div v-else>
                    <div class="p-text-center p-p-3">
                        <i class="pi pi-inbox" style="font-size: 2rem; color: #999;"></i>
                        <p class="p-mt-2">Sem linhas para exibir</p>
                    </div>
                </div>
            </template>
        </DataTable>

        <!-- Delete Confirmation Dialog -->
        <Dialog
            :visible.sync="deleteDialog"
            :style="{ width: '450px' }"
            header="Confirmar Eliminação"
            :modal="true"
        >
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem; color: var(--red-500);" />
                <span>
                    Tem certeza que deseja eliminar o dossiê <strong>{{ selectedDossier?.reference }}</strong>?
                    <br />Esta ação não pode ser desfeita.
                </span>
            </div>
            <template #footer>
                <Button
                    label="Cancelar"
                    icon="pi pi-times"
                    class="p-button-text"
                    @click="deleteDialog = false"
                />
                <Button
                    label="Eliminar"
                    icon="pi pi-check"
                    class="p-button-danger"
                    @click="deleteDossier"
                    :loading="deleting"
                />
            </template>
        </Dialog>

        <Dialog
            :visible.sync="convertDialog"
            :style="{ width: '520px' }"
            header="Converter Dossiê"
            :modal="true"
        >
            <div class="p-fluid">
                <div class="p-field">
                    <label for="convertTargetType">Tipo de destino</label>
                    <Dropdown
                        id="convertTargetType"
                        v-model="convertTargetType"
                        :options="availableConversionTypes"
                        optionLabel="name"
                        optionValue="id"
                        placeholder="Selecione o tipo destino"
                        appendTo="body"
                    />
                </div>
            </div>
            <template #footer>
                <Button
                    label="Cancelar"
                    icon="pi pi-times"
                    class="p-button-text"
                    @click="closeConvertDialog"
                />
                <Button
                    label="Abrir formulário"
                    icon="pi pi-check"
                    :disabled="!convertTargetType"
                    :loading="convertLoading"
                    @click="startConversion"
                />
            </template>
        </Dialog>

    </div>
</template>

<script>
import dossierService from '@/services/internalDossier.service';
import { FilterMatchMode } from 'primevue/api';
import PreviewInternalDossierReport from '@/components/internalDossier/reports/PreviewInternalDossierReport.vue';
import Tag from 'primevue/tag';

export default {
    name: "PatDossierList",
    props: {
        patId: {
            type: Number,
            required: true
        }
    },
    components: {
        Tag,
        PreviewInternalDossierReport
    },

    data() {
        return {
            tipodoc: null,
            obrano: '',
            dossierTypes: [],
            dossierList: [],
            loadingTypes: false,
            loading: false,
            deleting: false,
            convertDialog: false,
            convertLoading: false,
            convertSourceDossier: null,
            convertTargetType: null,

            filters: {
                dateFrom: null,
                dateTo: null
            },

            totalRecords: 0,
            currentPage: 1,
            perPage: 20,

            tableFilters: {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS }
            },

            deleteDialog: false,
            selectedDossier: null,
            expandedRows: [],
            expandedViewsFields: {}
        };
    },

    computed: {
        selectedTypeName() {
            if (!this.tipodoc) return null;
            const type = this.dossierTypes.find(t => t.id === this.tipodoc);
            return type?.name;
        },
        availableConversionTypes() {
            if (!this.convertSourceDossier) {
                return [];
            }

            return this.dossierTypes.filter(type => type.id !== this.convertSourceDossier.type_id);
        }
    },

    created() {
        this.loadDossierTypes();
        this.fetchDossiers();
    },

    methods: {
        onRowExpand(event) {
            this.expandedViewsFields = this.getViewExpandableRowsLines(event.data.type_id);
            this.expandedRows = event?.data ? [event.data] : [];
        },
        onRowCollapse() {
            this.expandedRows = [];
            this.expandedViewsFields = {};
        },
        async loadDossierTypes() {
            try {
                this.loadingTypes = true;
                this.dossierTypes = await dossierService.getConfigTypes();
            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao carregar tipos de dossier',
                    life: 3000
                });
            } finally {
                this.loadingTypes = false;
            }
        },

        getViewExpandableRowsLines(type) {
            let config = this.dossierTypes.find(t => t.id === type)?.view_fields || false;
            return config.lines || {};
        },

        async fetchDossiers(event = {}) {
            try {
                this.loading = true;

                const page = event.page ? event.page + 1 : 1;

                const params = {
                    page,
                    per_page: this.perPage
                };

                if (this.tipodoc) {
                    params.ndos = this.tipodoc;
                }

                if (this.obrano) {
                    params.obrano = this.obrano;
                }

                if (this.filters.dateFrom) {
                    params.date_from = this.formatDateForAPI(this.filters.dateFrom);
                }

                if (this.filters.dateTo) {
                    params.date_to = this.formatDateForAPI(this.filters.dateTo);
                }

                if (this.patId) {
                    params.orinopat = this.patId;
                }

                const response = await dossierService.getDossiers(params);

                this.dossierList = response.data;
                this.totalRecords = response.meta.total;
                this.currentPage = response.meta.current_page;

            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao carregar dossiês',
                    life: 3000
                });
            } finally {
                this.loading = false;
            }
        },

        fetchWithFilters() {
            this.fetchDossiers();
        },

        editDossier(dossier) {
            this.$emit('editDossier', {
                reference: dossier.reference,
                type: dossier.type_id
            });
        },

        openConvertDialog(dossier) {
            this.convertSourceDossier = dossier;
            this.convertTargetType = null;
            this.convertDialog = true;
        },

        closeConvertDialog() {
            this.convertDialog = false;
            this.convertLoading = false;
            this.convertSourceDossier = null;
            this.convertTargetType = null;
        },

        async startConversion() {
            if (!this.convertSourceDossier || !this.convertTargetType) {
                return;
            }

            try {
                this.convertLoading = true;
                const response = await dossierService.getConversionDraft(
                    this.convertSourceDossier.type_id,
                    this.convertTargetType,
                    this.convertSourceDossier.reference,
                );

                this.$emit('openConversionDraft', response);
                this.closeConvertDialog();
            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Não foi possível preparar a conversão do dossier',
                    life: 3000
                });
                this.convertLoading = false;
            }
        },

        confirmDelete(dossier) {
            this.selectedDossier = dossier;
            this.deleteDialog = true;
        },

        async deleteDossier() {
            try {
                this.deleting = true;

                await dossierService.deleteDossier(this.selectedDossier.id);

                this.$toast.add({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: 'Dossiê eliminado com sucesso',
                    life: 3000
                });

                this.deleteDialog = false;
                this.selectedDossier = null;

                await this.fetchDossiers();
            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao eliminar dossiê',
                    life: 3000
                });
            } finally {
                this.deleting = false;
            }
        },

        formatDate(dateString) {
            if (!dateString) return '-';

            const date = new Date(dateString);
            return date.toLocaleDateString('pt-PT', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        },

        formatDateForAPI(date) {
            if (!date) return null;

            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');

            return `${year}-${month}-${day}`;
        },
        openForm() {
            this.$emit('openForm');
        },
        formatCurrency(value) {
            return new Intl.NumberFormat('pt-PT', {
                style: 'currency',
                currency: 'EUR'
            }).format(value || 0);
        },
        formatDiscount(value) {
            return `${value || 0}%`;
        }
    }
};
</script>

<style scoped>
.card {
    padding: 2rem;
}

h1 {
    margin-bottom: 1.5rem;
    color: #495057;
}

.p-field label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

dl {
    display: grid;
    grid-template-columns: 120px 1fr;
    gap: 0.5rem;
    margin: 0;
}

dt {
    font-weight: 600;
    color: #6c757d;
}

dd {
    margin: 0;
}

.confirmation-content {
    display: flex;
    align-items: center;
    padding: 1rem 0;
}

pre {
    background: #f8f9fa;
    padding: 1rem;
    border-radius: 4px;
    overflow-x: auto;
}

.p-gap-2 {
    gap: 0.5rem;
}
</style>
